import { AnchorButton, Button } from "@svea/design-system-components-react";
import {
	AriaPopover,
	FormWithTracking,
	formatSsn,
	formatZip,
	Heading,
	isRequired,
	isRequiredEmail,
	isRequiredPhone,
	Markdown,
	ReactSelectField,
	SimpleGrid,
	Stack,
	TextField,
	useText,
	validateEmail,
	validatePhoneNumber,
} from "@sveabank/cc-common";
import type { FormApi } from "final-form";
import { Link, useNavigate } from "react-router";
import Content from "../../components/content";
import DotDivider from "../../components/dot-divider";
import PageNavigationFooter from "../../components/page-navigation-footer";
import { withLoader } from "../../hoc/with-loader";
import { usePageContext } from "../../hooks/context";
import { usePages } from "../../hooks/usePages";
import type { ContactFormValues } from "../../types/contact-information";
import CompanyInformation from "./company-information";
import { useContactForm } from "./hooks";

const ContactInfoPage = () => {
	const [t, texts] = useText();
	const { nextPage, prevPage } = usePages();
	const navigate = useNavigate();

	const { applicationDetails, countries, initialFormValues, submitContactInfo } =
		usePageContext(useContactForm);

	const handleSubmitAndNavigate = async (
		values: ContactFormValues,
		form: FormApi<ContactFormValues, Partial<ContactFormValues>>,
	) => {
		const state = form.getState();
		if (state.dirty) {
			await submitContactInfo(values);
			if (!nextPage) throw new Error("Tried navigating to nextPage without nextPage available");
			navigate(nextPage);
		} else {
			if (!nextPage) throw new Error("Tried navigating to nextPage without nextPage available");
			navigate(nextPage);
		}
	};

	return (
		<Content my={8}>
			<Stack gap={8}>
				<CompanyInformation details={applicationDetails} />

				<FormWithTracking<ContactFormValues>
					onSubmit={handleSubmitAndNavigate}
					initialValues={initialFormValues}
					matomoInstance={window.Matomo}
					render={({ handleSubmit, submitting }) => (
						<form onSubmit={handleSubmit} name="contactForm">
							<Stack gap={8}>
								<Stack component="section" gap={4}>
									<Stack direction="row" gap={2}>
										<Heading.H3 italic={false} size="base">
											{t(texts.contactInfo.companyContact.heading)}
										</Heading.H3>
										<AriaPopover
											variant="info"
											content={() => (
												<Markdown allowParagraphs>{t(texts.contactInfo.tooltip)}</Markdown>
											)}
										/>
									</Stack>
									<SimpleGrid cols={3}>
										<ReactSelectField
											testId="country-select"
											options={countries}
											label={t(texts.contactInfo.companyContact.countryCode)}
											ariaLabel={t(texts.contactInfo.companyContact.countryCode)}
											name="companyCountryCode"
											validators={[isRequired]}
											floatingLabel
											initialValue={initialFormValues.companyCountryCode || "+46"}
										/>
										<TextField
											label={t(texts.contactInfo.companyContact.phoneNumber)}
											type="tel"
											name="companyPhone"
											validators={[isRequiredPhone, validatePhoneNumber]}
										/>
										<TextField
											label={t(texts.common.email)}
											type="email"
											name="companyEmail"
											validators={[isRequiredEmail, validateEmail]}
										/>
									</SimpleGrid>
								</Stack>
								<Stack component="section" gap={2}>
									<Stack gap={2} direction="row">
										<Heading.H3 italic={false} size="base">
											{t(texts.contactInfo.applicantContact.heading)}
										</Heading.H3>

										<AriaPopover
											variant="info"
											content={() => (
												<Markdown allowParagraphs>{t(texts.contactInfo.tooltip)}</Markdown>
											)}
										/>
									</Stack>
									{applicationDetails.applicant.address && (
										<DotDivider size={1} color="text-primary">
											<div>{`${applicationDetails.applicant.firstName || ""} ${
												applicationDetails.applicant.lastName || ""
											}`}</div>
											<div>{formatSsn(applicationDetails.applicant.nationalId)}</div>
											<div>{applicationDetails.applicant.address.addressLine1}</div>
											<div>{formatZip(applicationDetails.applicant.address.zipCode)}</div>
											<div>{applicationDetails.applicant.address?.city}</div>
										</DotDivider>
									)}

									<SimpleGrid cols={3}>
										<ReactSelectField
											testId="country-select"
											options={countries}
											label={t(texts.contactInfo.companyContact.countryCode)}
											ariaLabel={t(texts.contactInfo.companyContact.countryCode)}
											name="countryCode"
											validators={[isRequired]}
											floatingLabel
											initialValue={initialFormValues.countryCode || "+46"}
										/>
										<TextField
											label={t(texts.contactInfo.companyContact.phoneNumber)}
											type="tel"
											name="phone"
											validators={[isRequiredPhone, validatePhoneNumber]}
										/>
										<TextField
											label={t(texts.common.email)}
											type="email"
											name="email"
											validators={[isRequiredEmail, validateEmail]}
										/>
									</SimpleGrid>
								</Stack>
							</Stack>
							<PageNavigationFooter>
								<Button variant="primary" type="submit" isPending={submitting}>
									{t(texts.common.continue)}
								</Button>
								{prevPage && (
									<AnchorButton as={Link} variant="secondary" to={prevPage}>
										{t(texts.common.goBack)}
									</AnchorButton>
								)}
							</PageNavigationFooter>
						</form>
					)}
				/>
			</Stack>
		</Content>
	);
};

export default withLoader(useContactForm, ContactInfoPage);
